import { useEffect, useRef } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Course, FleetPosition, Hole, LngLat } from '../lib/types';
import { bearingDeg, distanceM } from '../lib/geo';
import { CORRIDOR_WIDTH_M, circlePolygon, corridorMask, corridorPolygon, courseImageryMask, emptyFc, fc, holeSpine } from '../lib/shapes';
import { applyMapMode, oneMapStyle, type MapMode } from '../lib/basemap';
import { BUGGY_ICON_ID, buggyIcon } from '../lib/buggyIcon.ts';
import { applyTerrain } from '../lib/terrain.ts';
import { FEATURES } from '../lib/features.ts';
import { applyIndicators, INDICATOR_DEFAULTS, type Indicators } from '../lib/indicators.ts';

interface Props {
  /** Pen the map to the hole being played. */
  lockToHole: boolean;
  cartPaths: import('geojson').FeatureCollection | null;
  parked: LngLat | null;
  course: Course;
  /** null before a course has been chosen — the map shows position only. */
  hole: Hole | null;
  teeColour: string;
  position: LngLat | null;
  heading: number;
  aim: LngLat | null;
  onAimChange: (p: LngLat | null) => void;
  fleet: FleetPosition[];
  showFleet: boolean;
  /** Rotate so the player always plays "up the screen". */
  playerUp: boolean;
  /** Reports the map's compass bearing so the UI can draw a north needle. */
  onBearing?: (deg: number) => void;
  /** Hands the on-screen pan/zoom buttons a handle on the map. */
  onControls?: (c: MapControls) => void;
  /** Route to the next tee along the cart paths, if one could be found. */
  route: LngLat[] | null;
  showHazards: boolean;
  showCartPaths: boolean;
  /** Keep the buggy centred as it moves. */
  follow: boolean;
  /** Fired when the golfer drags or pinches — following must yield to them. */
  onFollowBreak?: () => void;
  /**
   * Road / satellite / hybrid on the ONE map. Driving to the course and playing
   * it are the same map in two modes, not two maps — see oneMapStyle().
   */
  mapMode?: MapMode;
  /** Chrome theme; only affects the road and label rasters. */
  theme?: 'dark' | 'light';
  /** What the map may draw. Set on the phone, obeyed on every surface. */
  indicators?: Indicators;
}

export interface MapControls {
  panBy: (dx: number, dy: number) => void;
  /** Pixel pan: the map follows the finger, so the delta is applied inverted. */
  panPx: (dx: number, dy: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** hole → closer → closest → hole. One CarPlay map button, three levels. */
  zoomCycle: () => void;
}

/**
 * Opening zoom. Close enough to see the buggy against individual trees and cart
 * paths — the Waze "there's my house" moment — rather than the whole hole from
 * above, which tells a golfer nothing they cannot see by looking up.
 */
const INITIAL_ZOOM = 18.2;

const SRC = {
  hole: 'src-hole',
  green: 'src-green',
  hazards: 'src-hazards',
  keepout: 'src-keepout',
  me: 'src-me',
  corridor: 'src-corridor',
  imageryMask: 'src-imagery-mask',
  mask: 'src-mask',
  ends: 'src-ends',
  cart: 'src-cart',
  route: 'src-route',
  parked: 'src-parked',
  aim: 'src-aim',
  fleet: 'src-fleet',
} as const;

export default function HoleMap(props: Props) {
  const { course, hole, teeColour, position, heading, aim, onAimChange, fleet, showFleet, playerUp,
    cartPaths, parked, lockToHole, onBearing, onControls, follow, onFollowBreak, route, showHazards, showCartPaths,
    mapMode = 'satellite', theme = 'dark', indicators = INDICATOR_DEFAULTS } = props;
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const ready = useRef(false);
  const onAimRef = useRef(onAimChange);
  onAimRef.current = onAimChange;
  const onBearingRef = useRef(onBearing);
  onBearingRef.current = onBearing;
  const onControlsRef = useRef(onControls);
  onControlsRef.current = onControls;
  const positionRef = useRef(position);
  positionRef.current = position;
  /** Cleared once we have framed a real GPS fix, not before. */
  const needsInitialZoom = useRef(true);
  const resizeObs = useRef<ResizeObserver | null>(null);
  const followRef = useRef(follow);
  followRef.current = follow;
  const onFollowBreakRef = useRef(onFollowBreak);
  onFollowBreakRef.current = onFollowBreak;
  /** Set while WE move the camera, so our own easeTo is not mistaken for a drag. */
  const selfMoving = useRef(false);

  // --- init ---------------------------------------------------------------
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new maplibregl.Map({
      container: el.current,
      style: oneMapStyle(theme, mapMode),
      // With no hole and no fix there is nothing to look at; the first fix
      // flies the camera in. Anywhere is as good as anywhere else until then.
      center: hole?.green.middle ?? position ?? [0, 0],
      zoom: hole || position ? 16.5 : 2,
      pitch: 50,
      attributionControl: { compact: true },
      // Head-unit touch screens are resistive on cheap units; rotation gestures
      // fight the auto-orientation and confuse drivers.
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
    });
    m.touchZoomRotate.disableRotation();
    map.current = m;

    // MapLibre caches the container size at construction and derives every
    // screen->ground conversion from it. The map now lives inside a flex track
    // that is sized AFTER construction, so without this the canvas and the
    // container disagree and a tap unprojects to the wrong place on the ground
    // — the aim point lands nowhere near your finger.
    const ro = new ResizeObserver(() => m.resize());
    ro.observe(el.current!);
    resizeObs.current = ro;

    m.on('move', () => onBearingRef.current?.(m.getBearing()));

    // A gesture from the golfer always beats follow mode. originalEvent is the
    // discriminator: it is present for a real drag or pinch and absent for our
    // own easeTo, which would otherwise cancel following the instant it began.
    const yieldToUser = (e: { originalEvent?: unknown }) => {
      if (e?.originalEvent && !selfMoving.current) onFollowBreakRef.current?.();
    };
    m.on('dragstart', yieldToUser);
    m.on('rotatestart', yieldToUser);
    m.on('touchmove', yieldToUser);

    m.on('load', () => {
      // Elevation first, before any overlay is added. The corridor, the green
      // and the hazards are fills, and MapLibre drapes fills onto whatever
      // terrain is present when it draws them — attaching relief afterwards
      // leaves the course furniture floating on the old flat plane.
      if (FEATURES.terrain) applyTerrain(m);

      const addGeo = (id: string) => m.addSource(id, { type: 'geojson', data: emptyFc });
      Object.values(SRC).forEach(addGeo);

      /*
        HYBRID: aerial ON the course, flat turf everywhere else.
        Added before any other layer so it covers the imagery but stays under
        the corridor, greens, hazards, cart paths and the buggy — all of which
        are added after this point and must keep full contrast on top of it.
      */
      m.addLayer({
        id: 'imagery-mask',
        type: 'fill',
        source: SRC.imageryMask,
        layout: { visibility: 'none' },
        paint: { 'fill-color': theme === 'light' ? '#dfe7df' : '#14241a', 'fill-opacity': 1 },
      });

      // The playing corridor: the strip of land this hole is played down.
      // Someone who has never played golf cannot tell where a hole begins or
      // ends from satellite imagery — fairways all look alike and run into one
      // another. This is the single thing that makes the map legible to them.
      /*
        Dim everything that is not this hole.
        Two stacked masks at different widths, because one hard-edged mask
        reads as a rendering fault rather than as focus: the near band lands
        at ~0.45 and the far field at ~0.85, so the hole being played is the
        only thing lit and attention falls off instead of stopping at a line.
        (Was 0.3 / 0.55: on the buggy's 10.1" in sunlight that read as nothing.
        CEO 2026-09-05: "grey out all the other holes except the one we're on.") Drawn beneath the course furniture, so cart paths
        and the buggy keep full contrast on top of it.
      */
      m.addLayer({
        id: 'mask-far',
        type: 'fill',
        source: SRC.mask,
        filter: ['==', ['get', 'band'], 'far'],
        paint: { 'fill-color': '#04120a', 'fill-opacity': 0.73 },
      });
      m.addLayer({
        id: 'mask-near',
        type: 'fill',
        source: SRC.mask,
        filter: ['==', ['get', 'band'], 'near'],
        paint: { 'fill-color': '#04120a', 'fill-opacity': 0.45 },
      });
      m.addLayer({
        id: 'corridor-fill',
        type: 'fill',
        source: SRC.corridor,
        paint: { 'fill-color': '#3d9bff', 'fill-opacity': 0.13 },
      });
      m.addLayer({
        id: 'corridor-line',
        type: 'line',
        source: SRC.corridor,
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#5cb0ff', 'line-width': 2.5, 'line-opacity': 0.9 },
      });
      m.addLayer({
        id: 'keepout-fill',
        type: 'fill',
        source: SRC.keepout,
        paint: {
          'fill-color': ['match', ['get', 'severity'], 'block', '#ff2f2f', '#ffb020'],
          'fill-opacity': 0.18,
        },
      });
      m.addLayer({
        id: 'keepout-line',
        type: 'line',
        source: SRC.keepout,
        paint: {
          'line-color': ['match', ['get', 'severity'], 'block', '#ff5a5a', '#ffc861'],
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      });
      m.addLayer({
        id: 'cart-path',
        type: 'line',
        source: SRC.cart,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ff4d4d',
          'line-width': ['interpolate', ['linear'], ['zoom'], 14, 2, 18, 6],
          'line-opacity': 0.85,
        },
      });
      m.addLayer({
        id: 'hole-line',
        type: 'line',
        source: SRC.hole,
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': '#f2f7f3', 'line-width': 3, 'line-opacity': 0.65, 'line-dasharray': [1.5, 1.5] },
      });
      m.addLayer({
        id: 'green-fill',
        type: 'fill',
        source: SRC.green,
        paint: { 'fill-color': '#5ce08a', 'fill-opacity': 0.28 },
      });
      m.addLayer({
        id: 'green-line',
        type: 'line',
        source: SRC.green,
        paint: { 'line-color': '#7dffae', 'line-width': 2 },
      });
      m.addLayer({
        id: 'hazard-dot',
        type: 'circle',
        source: SRC.hazards,
        paint: {
          'circle-radius': 9,
          'circle-color': [
            'match',
            ['get', 'kind'],
            'water', '#2f8fff',
            'bunker', '#e8d08a',
            'ob', '#ff4d4d',
            '#cbd5cc',
          ],
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 2,
        },
      });
      m.addLayer({
        id: 'hazard-label',
        type: 'symbol',
        source: SRC.hazards,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 13,
          'text-offset': [0, 1.4],
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#e9f2ea', 'text-halo-color': '#0b120d', 'text-halo-width': 1.5 },
      });
      m.addLayer({
        id: 'fleet-dot',
        type: 'circle',
        source: SRC.fleet,
        paint: {
          'circle-radius': 8,
          'circle-color': ['case', ['get', 'alert'], '#ff3b3b', '#8fb7ff'],
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 2,
        },
      });
      m.addLayer({
        id: 'fleet-label',
        type: 'symbol',
        source: SRC.fleet,
        layout: { 'text-field': ['get', 'buggyId'], 'text-size': 12, 'text-offset': [0, 1.3] },
        paint: { 'text-color': '#cfe0ff', 'text-halo-color': '#0b120d', 'text-halo-width': 1.5 },
      });
      m.addLayer({
        id: 'aim-line',
        type: 'line',
        source: SRC.aim,
        filter: ['==', '$type', 'LineString'],
        paint: { 'line-color': '#ffd23f', 'line-width': 2.5 },
      });
      m.addLayer({
        id: 'aim-dot',
        type: 'circle',
        source: SRC.aim,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': 11,
          'circle-color': '#ffd23f',
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 2,
        },
      });
      // The route to the next tee. A casing underneath keeps it readable over
      // both pale sand and dark trees without having to pick a colour that
      // works on neither.
      m.addLayer({
        id: 'route-casing',
        type: 'line',
        source: SRC.route,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#06210f', 'line-width': 11, 'line-opacity': 0.55 },
      });
      m.addLayer({
        id: 'route-line',
        type: 'line',
        source: SRC.route,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#38e07b', 'line-width': 6 },
      });
      m.addLayer({
        id: 'parked-dot',
        type: 'circle',
        source: SRC.parked,
        paint: {
          'circle-radius': 9,
          'circle-color': '#8fb7ff',
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 3,
        },
      });
      m.addLayer({
        id: 'parked-label',
        type: 'symbol',
        source: SRC.parked,
        layout: { 'text-field': 'BUGGY', 'text-size': 11, 'text-offset': [0, 1.5] },
        paint: { 'text-color': '#cfe0ff', 'text-halo-color': '#0b120d', 'text-halo-width': 1.6 },
      });
      m.addLayer({
        id: 'ends-ring',
        type: 'circle',
        source: SRC.ends,
        paint: {
          'circle-radius': 13,
          'circle-color': 'rgba(61,155,255,0.25)',
          'circle-stroke-color': '#5cb0ff',
          'circle-stroke-width': 3,
        },
      });
      m.addLayer({
        id: 'ends-label',
        type: 'symbol',
        source: SRC.ends,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 13,
          'text-offset': [0, 1.8],
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#cfe8ff', 'text-halo-color': '#0b120d', 'text-halo-width': 2 },
      });
      // The buggy marker. icon-rotation-alignment 'map' is what makes the
      // heading correct: icon-rotate is a TRUE bearing, and MapLibre applies
      // the map's own rotation on top. Set it to 'viewport' and the buggy would
      // point the wrong way the moment the map rotated to player-up.
      try {
        if (!m.hasImage(BUGGY_ICON_ID)) {
          m.addImage(BUGGY_ICON_ID, buggyIcon(), { pixelRatio: 3 });
        }
      } catch {
        /* canvas unavailable — the fallback circle below still shows position */
      }
      // Accuracy halo, then the blue position dot. Both stay visible under the
      // buggy icon: if the icon ever fails to rasterise, position must not
      // vanish with it.
      m.addLayer({
        id: 'me-halo',
        type: 'circle',
        source: SRC.me,
        paint: {
          'circle-radius': 26,
          'circle-color': '#2f7fff',
          'circle-opacity': 0.16,
          'circle-stroke-color': '#2f7fff',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 0.35,
        },
      });
      m.addLayer({
        id: 'me-dot',
        type: 'circle',
        source: SRC.me,
        paint: {
          'circle-radius': 8,
          'circle-color': '#2f7fff',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
        },
      });
      m.addLayer({
        id: 'me-buggy',
        type: 'symbol',
        source: SRC.me,
        layout: {
          'icon-image': BUGGY_ICON_ID,
          'icon-size': 0.42,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'icon-pitch-alignment': 'map',
        },
      });

      // Tap anywhere to drop/move the aim marker.
      m.on('click', (e) => onAimRef.current([e.lngLat.lng, e.lngLat.lat]));

      ready.current = true;
      onBearingRef.current?.(m.getBearing());
      onControlsRef.current?.({
        // Pan by a fraction of the viewport rather than a fixed pixel count, so
        // one tap moves the same proportion of the screen on a 7" head unit as
        // on a phone.
        panBy: (dx, dy) => {
          // A deliberate pan is the golfer choosing to look elsewhere.
          onFollowBreakRef.current?.();
          const c = m.getContainer();
          m.panBy([dx * c.clientWidth * 0.45, dy * c.clientHeight * 0.45], { duration: 260 });
        },
        panPx: (dx, dy) => {
          onFollowBreakRef.current?.();
          // No animation: the deltas arrive at gesture rate and must track the finger.
          m.panBy([-dx, -dy], { duration: 0 });
        },
        zoomIn: () => m.zoomIn({ duration: 220 }),
        zoomOut: () => m.zoomOut({ duration: 220 }),
        zoomCycle: () => {
          const levels = [INITIAL_ZOOM, INITIAL_ZOOM + 1.3, INITIAL_ZOOM + 2.6];
          const z = m.getZoom();
          const next = levels.find((l) => l > z + 0.15) ?? levels[0];
          m.zoomTo(next, { duration: 260 });
        },
      });
      // Kick a first paint of the hole geometry.
      m.fire('fairway:ready');
    });

    return () => {
      ready.current = false;
      resizeObs.current?.disconnect();
      resizeObs.current = null;
      m.remove();
      map.current = null;
    };
    // Deliberately mount-once; all updates flow through the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- map mode: road / satellite / hybrid on the ONE map -----------------
  // Visibility flip, never setStyle: the terrain, corridor, greens, hazards,
  // cart paths and fleet all live on this map and a restyle would drop them.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (ready.current) {
      applyMapMode(m, mapMode);
      return;
    }
    // 🟥 Was `if (!ready.current) return;` and it silently dropped the change.
    // A mode picked before the style finished loading — which includes the very
    // first render, and any toggle on a slow head unit — was thrown away with
    // nothing to retry it, so the switch appeared to do nothing at all.
    const apply = () => applyMapMode(m, mapMode);
    m.once('fairway:ready', apply);
    return () => {
      m.off('fairway:ready', apply);
    };
  }, [mapMode]);

  // Hybrid = imagery clipped to the course. MapLibre cannot clip a raster to a
  // shape, so the imagery is drawn full-bleed and this covers everything that
  // is NOT a fairway — same picture, one fill layer.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return;
    const on = mapMode === 'hybrid' && course.holes.length > 0;
    if (m.getLayer('imagery-mask')) {
      m.setLayoutProperty('imagery-mask', 'visibility', on ? 'visible' : 'none');
    }
    if (!on) return;
    const src = m.getSource(SRC.imageryMask) as GeoJSONSource | undefined;
    src?.setData(courseImageryMask(course.holes, ((h: Hole) => holeSpine(h, teeColour)) as never));
  }, [mapMode, course, teeColour]);

  // Indicators are a visibility sweep rather than a prop per drawable: the same
  // map renders on four surfaces now, and twenty-odd props would drift apart.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (ready.current) { applyIndicators(m, indicators); return; }
    const apply = () => applyIndicators(m, indicators);
    m.once('fairway:ready', apply);
    return () => { m.off('fairway:ready', apply); };
  }, [indicators]);

  const setData = (id: string, data: Parameters<GeoJSONSource['setData']>[0]) => {
    const m = map.current;
    if (!m || !ready.current) return;
    (m.getSource(id) as GeoJSONSource | undefined)?.setData(data);
  };

  // --- static hole geometry ----------------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const paint = () => {
      // No course chosen yet: clear every hole-derived layer and leave the map
      // free to roam, so what remains is a plain satellite view of where the
      // golfer is standing.
      if (!hole) {
        [SRC.hole, SRC.green, SRC.corridor, SRC.mask, SRC.ends, SRC.hazards, SRC.keepout].forEach((s) =>
          setData(s, emptyFc)
        );
        m.setMaxBounds(null);
        m.setMinZoom(2);
        m.setMaxZoom(20);
        return;
      }
      const tee = hole.tees[teeColour] ?? hole.tees.White;
      setData(SRC.hole, fc([
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [tee, hole.green.middle] },
        },
      ]));
      setData(SRC.green, fc([circlePolygon(hole.green.middle, 16, { hole: hole.number })]));

      const spine = holeSpine(hole, teeColour);
      setData(
        SRC.corridor,
        fc([corridorPolygon(spine, CORRIDOR_WIDTH_M, { hole: hole.number })])
      );
      /*
        Dim everything except the hole your GPS says you are on.

        Gated on having a fix, NOT on the manual hole lock: the hole shown here
        is the one the detector picked from position, so the moment we know
        where you are standing we can focus on it — and it re-chunks by itself
        as you drive onto the next one. Without a fix there is no "your hole"
        to focus on, and a mask with no subject just makes the course dark, so
        it falls back to plain satellite.
      */
      setData(SRC.mask, !position ? emptyFc : fc([
        corridorMask(spine, CORRIDOR_WIDTH_M * 2.6, { band: 'far' }),
        corridorMask(spine, CORRIDOR_WIDTH_M * 1.5, { band: 'near' }),
      ]));
      setData(SRC.ends, fc([
        { type: 'Feature', properties: { label: 'TEE' }, geometry: { type: 'Point', coordinates: tee } },
        {
          type: 'Feature',
          properties: { label: 'GREEN' },
          geometry: { type: 'Point', coordinates: hole.green.middle },
        },
      ]));
      setData(
        SRC.hazards,
        !showHazards ? emptyFc : fc(
          hole.hazards.map<Feature>((h) => ({
            type: 'Feature',
            properties: { kind: h.kind, label: `${h.label} · ${Math.round(distanceM(tee, h.point))}m` },
            geometry: { type: 'Point', coordinates: h.point },
          }))
        )
      );
      const near = course.keepOut.filter((z) => distanceM(z.centre, hole.green.middle) < 600);
      setData(SRC.keepout, fc(near.map((z) => circlePolygon(z.centre, z.radius, { severity: z.severity }))));

      const box = [tee, hole.green.front, hole.green.middle, hole.green.back,
                   ...hole.hazards.map((h) => h.point)];
      const sw: [number, number] = [
        Math.min(...box.map((p) => p[0])),
        Math.min(...box.map((p) => p[1])),
      ];
      const ne: [number, number] = [
        Math.max(...box.map((p) => p[0])),
        Math.max(...box.map((p) => p[1])),
      ];

      // Pen the viewport to this hole plus a margin.
      //
      // Two reasons, and the second is the one that matters at fleet scale:
      //   1. a golfer who pans away is lost and has to find their way back
      //   2. an unbounded map will happily stream tiles for the whole country.
      //      Penned to one hole, a full round loads a few hundred tiles and the
      //      service worker's cache stays bounded; unbounded, idle panning can
      //      blow through it and evict the tiles for the course being played.
      if (lockToHole) {
        const padDeg = 0.0035; // ~350-400m, enough to see the next tee
        m.setMaxBounds([
          [sw[0] - padDeg, sw[1] - padDeg],
          [ne[0] + padDeg, ne[1] + padDeg],
        ]);
        m.setMinZoom(14.5);
        m.setMaxZoom(19);
      } else {
        m.setMaxBounds(null);
        m.setMinZoom(2);
        m.setMaxZoom(20);
      }

      // Waze opens on you, close in, so you can see where you are before you
      // set off. Only fall back to framing the whole hole when there is no fix
      // — that is the one case where "where am I" has no answer.
      const playBearing = bearingDeg(tee, hole.green.middle);
      if (needsInitialZoom.current && positionRef.current) {
        m.easeTo({ center: positionRef.current, zoom: INITIAL_ZOOM, bearing: playBearing, duration: 500 });
        needsInitialZoom.current = false;
      } else if (!needsInitialZoom.current) {
        // Changing hole once underway: frame the new hole.
        m.fitBounds([sw, ne], {
          padding: { top: 90, bottom: 190, left: 60, right: 60 },
          duration: 600,
          bearing: playBearing,
        });
      } else {
        // No fix yet. Sit on the tee at playing zoom rather than pulling out to
        // the whole hole — the first fix will arrive and recentre in a moment,
        // and a wide shot followed by a snap-in looks like a glitch.
        m.easeTo({ center: tee, zoom: INITIAL_ZOOM, bearing: playBearing, duration: 400 });
      }
    };
    if (ready.current) paint();
    else m.once('fairway:ready', paint);
  }, [hole, teeColour, course.keepOut, lockToHole, showHazards]);

  // --- live position ------------------------------------------------------
  useEffect(() => {
    if (!position) {
      setData(SRC.me, emptyFc);
      return;
    }
    setData(SRC.me, fc([
      {
        type: 'Feature',
        properties: { heading: Number.isFinite(heading) ? heading : 0 },
        geometry: { type: 'Point', coordinates: position },
      },
    ]));

    const m = map.current;
    if (!m || !ready.current) return;

    // The first genuine fix is what we zoom to. It almost always arrives AFTER
    // the hole geometry has painted, so this cannot live in that effect.
    if (needsInitialZoom.current) {
      needsInitialZoom.current = false;
      selfMoving.current = true;
      m.easeTo({
        center: position,
        zoom: INITIAL_ZOOM,
        // Nothing to play towards until a course is picked, so hold north up.
        bearing: hole ? bearingDeg(position, hole.green.middle) : 0,
        duration: 600,
      });
      window.setTimeout(() => {
        selfMoving.current = false;
      }, 700);
      return;
    }

    const playBearing = hole ? bearingDeg(position, hole.green.middle) : null;

    if (followRef.current) {
      selfMoving.current = true;
      m.easeTo({
        center: position,
        ...(playerUp && playBearing != null ? { bearing: playBearing } : {}),
        duration: 700,
      });
      // Clear the guard after the animation, not before, or the tail of our own
      // easeTo reads as a user drag.
      window.setTimeout(() => {
        selfMoving.current = false;
      }, 800);
    } else if (playerUp && playBearing != null) {
      m.easeTo({ bearing: playBearing, duration: 900 });
    }
  }, [position, playerUp, hole, heading]);

  // --- aim marker ---------------------------------------------------------
  useEffect(() => {
    if (!aim || !hole) {
      setData(SRC.aim, emptyFc);
      return;
    }
    const from = position ?? hole.tees[teeColour] ?? hole.tees.White;
    setData(SRC.aim, fc([
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: [from, aim, hole.green.middle] },
      },
      { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: aim } },
    ]));
  }, [aim, position, hole, teeColour]);

  // --- cart paths + parked marker -----------------------------------------
  useEffect(() => {
    setData(SRC.cart, showCartPaths ? (cartPaths ?? emptyFc) : emptyFc);
  }, [cartPaths, showCartPaths]);

  useEffect(() => {
    setData(
      SRC.parked,
      parked
        ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: parked } }])
        : emptyFc
    );
  }, [parked]);

  useEffect(() => {
    setData(
      SRC.route,
      route && route.length >= 2
        ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route } }])
        : emptyFc
    );
  }, [route]);

  // --- other buggies ------------------------------------------------------
  useEffect(() => {
    if (!showFleet) {
      setData(SRC.fleet, emptyFc);
      return;
    }
    setData(
      SRC.fleet,
      fc(
        fleet.map<Feature>((p) => ({
          type: 'Feature',
          properties: { buggyId: p.buggyId, alert: !!p.alert },
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        }))
      )
    );
  }, [fleet, showFleet]);

  return <div ref={el} className="map" />;
}
